import { useTranslation } from "react-i18next";

import type { MyRobotDetail } from "@/entities/user-robot";
import { formatMoney, formatPercent, moneySign } from "@/shared/lib";
import { Button, Panel } from "@/shared/ui";

/**
 * «Робот работает» вместо кнопки запуска (ТЗ §17).
 *
 * Второго запуска того же робота отсюда нет намеренно. Бэкенд его не
 * запрещает, но наращивать капитал придумано пополнение (§18): две покупки
 * одного робота дали бы в «Моих роботах» две истории и два ROI там, где
 * пользователь ждёт одну цифру.
 */
export function RunningStateBlock({
  robot,
  onTopup,
}: {
  robot: MyRobotDetail;
  onTopup: () => void;
}) {
  const { t } = useTranslation();

  const sign = moneySign(robot.earned_total);
  const tone =
    sign < 0 ? "text-status-loss" : sign > 0 ? "text-status-profit" : "text-text-secondary";
  const active = robot.status === "active";

  const rows: { label: string; value: string; tone?: string }[] = [
    {
      label: t("myRobots.capital.connected"),
      value: formatMoney(robot.capital_connected, { suffix: "USDT" }),
    },
    {
      label: t("myRobots.earned"),
      value: formatMoney(robot.earned_total, { sign: "always", suffix: "USDT" }),
      tone,
    },
    { label: t("myRobots.roi"), value: formatPercent(robot.earned_pct, { sign: "always" }), tone },
    { label: t("myRobots.daysRunning"), value: t("myRobots.days", { count: robot.days_running }) },
  ];

  return (
    <Panel as="section" tone="green" radius="lg" className="flex flex-col gap-3 p-4">
      <h2 className="text-body font-semibold text-text-primary">
        {t(active ? "myRobots.running" : "myRobots.status.stopped", { name: robot.robot.name })}
      </h2>

      <dl className="flex flex-col">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={[
              "flex items-center justify-between gap-3 py-2",
              index > 0 ? "border-t border-border-subtle" : "",
            ].join(" ")}
          >
            <dt className="text-caption font-normal text-text-secondary">{row.label}</dt>
            <dd className={["text-caption font-semibold", row.tone ?? "text-text-primary"].join(" ")}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      {active && robot.robot.topup_enabled ? (
        <Button size="cta" block onClick={onTopup}>
          {t("myRobots.topup.action")}
        </Button>
      ) : null}
    </Panel>
  );
}
